import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, Pencil, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { api, type Substation, type SubstationUpsertRequest } from "@/lib/api";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CatalogChoice } from "@/components/catalog-choice";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import { useToast } from "@/components/ui/toast";
import { ListFilter } from "@/components/list-filter";
import { AdminListLayout, AdminFilterPanel, AdminFilterGroup } from "@/components/admin-filter-panel";
import { ListPagination, ListSearch, ListSort, useBrowsingState, usePagedList } from "@/components/list-controls";
import { errorStep, FormStepNavigation } from "@/components/form-step-navigation";
import { useDialogDraft } from "@/components/dialog-draft";

const metadataFields = [
  ["address_line", "Physical address"],
  ["city", "City"],
  ["state", "Physical state"],
  ["served_states", "States served (separate with semicolons)"],
  ["tcn_region", "TCN region"],
  ["tcn_subregion", "TCN subregion"],
  ["transmission_station", "Transmission station"],
  ["injection_substation", "Injection substation"],
  ["latitude", "Latitude"],
  ["longitude", "Longitude"],
] as const;
type MetadataField = typeof metadataFields[number][0];
const emptyMetadata: Record<MetadataField, string> = {
  address_line: "", city: "", state: "",
  served_states: "", tcn_region: "", tcn_subregion: "",
  transmission_station: "", injection_substation: "", latitude: "", longitude: "",
};

export function SubstationsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { me } = useAuth();
  const canCreateFeeder = hasCapability(me, "catalog:write");
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [name, setName] = useState("");
  const [stationType, setStationType] = useState<"TS" | "ISS" | null>(null);
  const [stationVoltage, setStationVoltage] = useState<number | null>(null);
  const [externalKey, setExternalKey] = useState("");
  const [companyFilter, setCompanyFilter] = useBrowsingState("substations:company", "");
  const [stateFilter, setStateFilter] = useBrowsingState("substations:state", "");
  const [physicalStateFilter, setPhysicalStateFilter] = useBrowsingState("substations:physical-state", "");
  const [regionFilter, setRegionFilter] = useBrowsingState("substations:region", "");
  const [typeFilter, setTypeFilter] = useBrowsingState("substations:type", "");
  const [editing, setEditing] = useState<Substation | null>(null);
  const [metadata, setMetadata] = useState(emptyMetadata);
  const [step, setStep] = useState(0);
  const draft = useDialogDraft();
  const resetDraft = draft.reset;
  useEffect(() => {
    if (!open) resetDraft();
  }, [open, resetDraft]);
  const saveRequested = useRef(false);
  const organizations = useQuery({ queryKey: ["tenants"], queryFn: () => api.tenants() });
  const stationOptions = useQuery({ queryKey: ["substations"], queryFn: () => api.substations() });
  const options = useQuery({ queryKey: ["grid-options"], queryFn: () => api.gridOptions() });
  const companyStations = (stationOptions.data ?? []).filter((station) =>
    !companyFilter || String(station.distribution_company_id) === companyFilter);
  const regionStations = companyStations.filter((station) => !regionFilter || station.tcn_region === regionFilter);
  const { list, query: substations } = usePagedList("substations", {
    distribution_company_id: companyFilter, served_state: stateFilter, state: physicalStateFilter,
    station_type: typeFilter, region: regionFilter,
  }, [
    { key: "name", label: "Name" }, { key: "company", label: "Distribution company" },
    { key: "external_key", label: "External key" }, { key: "type", label: "Type" }, { key: "region", label: "Region" },
  ], api.browseSubstations);
  const companies = useMemo(
    () => (organizations.data ?? []).filter((organization) => organization.kind === "distribution_company"),
    [organizations.data],
  );
  const coordinateError = (metadata.latitude.trim() === "") !== (metadata.longitude.trim() === "") ||
    (metadata.latitude.trim() !== "" && (!Number.isFinite(Number(metadata.latitude)) || Math.abs(Number(metadata.latitude)) > 90 ||
      !Number.isFinite(Number(metadata.longitude)) || Math.abs(Number(metadata.longitude)) > 180));
  function resetForm(): void {
    setStep(0);
    create.reset();
    setEditing(null);
    setCompanyId("");
    setName("");
    setStationType(null);
    setStationVoltage(null);
    setExternalKey("");
    setMetadata(emptyMetadata);
  }
  const create = useMutation({
    mutationFn: () => {
      const body: SubstationUpsertRequest = {
        distribution_company_id: Number(companyId), base_name: name.trim(), station_type: stationType, external_key: externalKey.trim(),
        voltage_kv: stationVoltage,
        address_line: metadata.address_line.trim() || null,
        city: metadata.city.trim() || null,
        state: metadata.state.trim() || null,
        served_states: metadata.served_states.split(";").map((state) => state.trim()).filter(Boolean),
        tcn_region: metadata.tcn_region.trim() || null,
        tcn_subregion: metadata.tcn_subregion.trim() || null,
        transmission_station: metadata.transmission_station.trim() || null,
        injection_substation: metadata.injection_substation.trim() || null,
        latitude: metadata.latitude.trim() ? Number(metadata.latitude) : null,
        longitude: metadata.longitude.trim() ? Number(metadata.longitude) : null,
      };
      return editing ? api.updateSubstation(editing.substation_id, body) : api.createSubstation(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["substations"] });
      void queryClient.invalidateQueries({ queryKey: ["feeders"] });
      setOpen(false);
      setCompanyId("");
      setName("");
      setExternalKey("");
      setStationType(null);
      setStationVoltage(null);
      setMetadata(emptyMetadata);
      setEditing(null);
      setStep(0);
      draft.reset();
      toast({ title: "Substation saved", variant: "success" });
    },
    onError: (error: unknown) => {
      setStep(errorStep(error, [
        ["distribution_company_id", "base_name", "station_type", "voltage_kv", "external_key", "name"],
        ["address_line", "city", "state", "latitude", "longitude"],
        ["served_states", "tcn_region", "tcn_subregion", "transmission_station", "injection_substation"],
      ]) ?? 2);
      toast({
      title: "Could not create substation",
      description: error instanceof Error ? error.message : "Unknown error",
      variant: "destructive",
      });
    },
    onSettled: () => { saveRequested.current = false; },
  });
  return (
    <>
      <PageHeader
        title="Substations"
        description="Substations belong to a distribution company and contain feeder templates."
        actions={
          <>
            <Button asChild variant="outline"><Link to="/imports?type=substations">Import data</Link></Button>
            <Dialog open={open} onOpenChange={(next) => {
              if (next) { draft.reset(); setOpen(true); }
              else if (draft.canClose(create.isPending)) { setOpen(false); resetForm(); }
            }}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4" /> Add substation</Button></DialogTrigger>
              <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-2xl"
                onChangeCapture={draft.onChangeCapture} onClickCapture={draft.onClickCapture} onKeyDownCapture={draft.onKeyDownCapture}>
                <DialogHeader><DialogTitle>{editing ? "Edit substation" : "Add substation"}</DialogTitle><DialogDescription>Company ownership, regional metadata, and location.</DialogDescription></DialogHeader>
                <div className="min-h-0 space-y-4 overflow-y-auto">
                {step === 0 ? <>
                <h3 className="font-semibold">Identity and ownership</h3>
                <ListFilter label="Distribution company" value={companyId} emptyLabel="Select company"
                  options={companies.map((company) => ({ value: String(company.tenant_id), label: company.name }))}
                  onChange={setCompanyId} />
                <div className="space-y-2"><Label htmlFor="substation-name">Base name</Label><Input id="substation-name" value={name} onChange={(event) => setName(event.target.value)} /></div>
                <CatalogChoice label="Station type" options={options.data?.station_types ?? []} value={stationType ? [stationType] : []}
                  onChange={(values) => setStationType((values[0] as "TS" | "ISS") || null)} />
                <CatalogChoice label="Station voltage (kV)" options={(options.data?.voltage_kv ?? []).filter(Boolean).map((value) => `${value}kV`)}
                  value={stationVoltage === null ? [] : [`${stationVoltage}kV`]}
                  onChange={(values) => setStationVoltage(values[0] ? Number(values[0].replace(/kV$/, "")) : null)} />
                {editing ? <p className="text-xs text-muted-foreground">Current saved name: {editing.name}</p> : null}
                <div className="space-y-2"><Label htmlFor="substation-key">External key</Label><Input id="substation-key" value={externalKey} onChange={(event) => setExternalKey(event.target.value)} placeholder="lagos-central" readOnly={Boolean(editing)} /></div>
                </> : null}
                {step === 1 ? <h3 className="font-semibold">Physical location</h3> : null}
                {step === 1 ? <>
                <p className="text-xs text-muted-foreground">Physical address, city, state, and GPS describe this station. States served describe coverage only; they do not determine its physical location.</p>
                </> : null}
                {(step === 0 ? [] : [step === 1 ? "physical" : "coverage"]).map((group) => <fieldset key={group} className="grid gap-3 sm:grid-cols-2">
                  {group === "coverage" ? <legend className="mb-3 text-sm font-semibold">Advanced: service coverage and regional metadata</legend> : null}
                  {metadataFields.filter(([field]) => ["address_line", "city", "state", "latitude", "longitude"].includes(field) === (group === "physical")).map(([field, label]) => (
                    <div key={field} className="space-y-2">
                      {field !== "served_states" && field !== "state" ? <Label htmlFor={`substation-${field}`}>{label}</Label> : null}
                      {field === "state" ? <CatalogChoice label="Physical state" options={options.data?.states ?? []}
                        value={metadata.state ? [metadata.state] : []} onChange={(values) => setMetadata({ ...metadata, state: values[0] ?? "" })} />
                        : field === "served_states" ? <CatalogChoice label="States served (service coverage)" multiple options={options.data?.states ?? []}
                        value={metadata.served_states.split(";").map((value) => value.trim()).filter(Boolean)}
                        onChange={(values) => setMetadata({ ...metadata, served_states: values.join(";") })} /> : <Input id={`substation-${field}`} value={metadata[field]}
                        type={field === "latitude" || field === "longitude" ? "number" : "text"}
                        step="any" onChange={(event) => setMetadata({ ...metadata, [field]: event.target.value })} />}
                    </div>
                  ))}
                </fieldset>)}
                {step === 1 && coordinateError ? <p role="alert" className="text-sm text-destructive">Enter both coordinates: latitude -90 to 90, longitude -180 to 180.</p> : null}
                {step === 2 ? <section aria-label="Review substation" className="space-y-2 rounded-md border p-3 text-sm">
                  <h3 className="font-semibold">Review</h3>
                  <p>Edited name: {name.trim()}</p>
                  <p>Station type: {stationType ?? "Unknown"}</p>
                  <p>Station voltage: {stationVoltage === null ? "Unknown" : `${stationVoltage}kV`}</p>
                  <p>Current saved name: {editing?.name ?? "New station"}</p>
                  <p>External key: {externalKey}</p>
                  <p>Physical location: {[metadata.address_line, metadata.city, metadata.state].filter(Boolean).join(", ") || "Unknown"}</p>
                  <p>GPS: {metadata.latitude !== "" ? `${metadata.latitude}, ${metadata.longitude}` : "Unknown"}</p>
                  {editing ? <div>
                    <h4 className="font-semibold">Changed values</h4>
                    {[
                      ["Company", editing.distribution_company_id, companyId],
                      ["Base name", editing.base_name ?? editing.name, name],
                      ["Type", editing.station_type ?? "", stationType ?? ""],
                      ["Voltage (kV)", editing.voltage_kv ?? "", stationVoltage ?? ""],
                      ...metadataFields.map(([field, label]) => [label,
                        field === "served_states" ? (editing.served_states ?? []).join(";") : editing[field] ?? "",
                        metadata[field],
                      ]),
                    ].filter(([, before, after]) => String(before) !== String(after)).map(([label, before, after]) =>
                      <p key={String(label)}>{label}: {String(before) || "Unknown"} → {String(after) || "Unknown"}</p>)}
                  </div> : null}
                </section> : null}
                {create.error ? <p role="alert" className="text-sm text-destructive">{create.error instanceof Error ? create.error.message : "Could not save substation."}</p> : null}
                </div>
                <FormStepNavigation steps={["Identity & Type", "Physical Location", "Service Coverage & Review"]} step={step}
                  onBack={() => setStep(step - 1)} onNext={() => setStep(step + 1)}
                  nextDisabled={step === 0 ? !companyId || !name.trim() || !externalKey.trim() : coordinateError} busy={create.isPending} />
                <DialogFooter><Button type="button" variant="outline" disabled={create.isPending} onClick={() => { if (draft.canClose(create.isPending)) { setOpen(false); resetForm(); } }}>Cancel</Button>{step === 2 ? <Button type="button" disabled={!companyId || !name.trim() || !externalKey.trim() || coordinateError || create.isPending} onClick={() => {
                  if (!create.isPending && !saveRequested.current) { saveRequested.current = true; create.mutate(); }
                }}>{create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save substation</Button> : null}</DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      {options.error ? <p role="alert" className="mb-4 text-sm text-destructive">Could not load shared grid choices. Saved values remain available; reload before selecting new metadata.</p> : null}
      <AdminListLayout filters={
        <AdminFilterPanel count={[companyFilter, stateFilter, physicalStateFilter, regionFilter, typeFilter, list.search.trim()].filter(Boolean).length}
          onClear={() => { setCompanyFilter(""); setStateFilter(""); setPhysicalStateFilter(""); setRegionFilter(""); setTypeFilter(""); list.setSearch(""); }}>
          <AdminFilterGroup label="Ownership">
            <ListFilter label="Distribution company filter" value={companyFilter}
              options={companies.map((company) => ({ value: String(company.tenant_id), label: company.name }))}
              onChange={(value) => { setCompanyFilter(value); setStateFilter(""); setRegionFilter(""); list.setPage(1); }} />
          </AdminFilterGroup>
          <AdminFilterGroup label="Location">
            <ListFilter label="Physical state filter" value={physicalStateFilter}
              options={(options.data?.states ?? []).filter(Boolean).map((value) => ({ value, label: value }))}
              onChange={(value) => { setPhysicalStateFilter(value); list.setPage(1); }} />
            <ListFilter label="Station type filter" value={typeFilter}
              options={[{ value: "unknown", label: "Unknown" }, ...(options.data?.station_types ?? []).filter(Boolean).map((value) => ({ value, label: value }))]}
              onChange={(value) => { setTypeFilter(value); list.setPage(1); }} />
            <ListFilter label="TCN region filter" value={regionFilter}
              options={Array.from(new Set(companyStations.map((station) => station.tcn_region).filter((value): value is string => Boolean(value)))).sort().map((value) => ({ value, label: value }))}
              onChange={(value) => { setRegionFilter(value); setStateFilter(""); list.setPage(1); }} />
            <ListFilter label="Service coverage state filter" value={stateFilter}
              options={Array.from(new Set(regionStations.flatMap((station) => station.served_states ?? []))).sort().map((value) => ({ value, label: value }))}
              onChange={(value) => { setStateFilter(value); list.setPage(1); }} />
          </AdminFilterGroup>
          <AdminFilterGroup label="Search">
            <ListSearch label="Search substations" placeholder="Name, company, external key…" value={list.search} onChange={list.setSearch} />
            <ListSort {...list} />
          </AdminFilterGroup>
        </AdminFilterPanel>
      }>
      {substations.error ? (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load substations: {substations.error instanceof Error ? substations.error.message : "Unknown error"}
        </div>
      ) : null}
      {organizations.error ? (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load distribution companies.
        </div>
      ) : null}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Voltage</th>
                <th className="px-4 py-3">Distribution company</th>
                <th className="px-4 py-3">External key</th>
                <th className="px-4 py-3">Region / states</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {substations.isLoading ? (
                <tr><td className="px-4 py-8 text-center text-muted-foreground" colSpan={7}>Loading substations…</td></tr>
              ) : substations.error && !substations.data ? (
                <tr><td className="px-4 py-8 text-center" colSpan={7}>
                  Unable to load substations.{" "}
                  <Button variant="outline" size="sm" onClick={() => void substations.refetch()}>Retry</Button>
                </td></tr>
              ) : list.total > 0 ? (
                list.rows.map((substation) => (
                  <tr key={substation.substation_id} className="border-b border-border">
                    <td className="px-4 py-3 font-medium"><Building2 className="mr-2 inline h-4 w-4 text-muted-foreground" />{substation.name}</td>
                    <td className="px-4 py-3">{substation.station_type ?? "Unknown"}</td>
                    <td className="px-4 py-3">{substation.voltage_kv == null ? "Unknown" : `${substation.voltage_kv}kV`}</td>
                    <td className="px-4 py-3">{substation.distribution_company_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{substation.external_key}</td>
                    <td className="px-4 py-3">{[substation.tcn_region, substation.served_states?.join(", ")].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-2">
                    <Button asChild size="sm" variant="outline"><Link to="/feeders" state={{ substationId: substation.substation_id }}>View feeders</Link></Button>
                    {canCreateFeeder ? <Button asChild size="sm" variant="outline"><Link to="/feeders" state={{ substationId: substation.substation_id, createFeeder: true }}>Add feeder</Link></Button> : null}
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditing(substation);
                      setCompanyId(String(substation.distribution_company_id));
                      setName(substation.base_name ?? substation.name);
                      setStationType(substation.station_type ?? null);
                      setStationVoltage(substation.voltage_kv ?? null);
                      setExternalKey(substation.external_key);
                      setMetadata(Object.fromEntries(metadataFields.map(([field]) => [
                        field, field === "served_states" ? (substation.served_states ?? []).join("; ") : String(substation[field] ?? ""),
                      ])) as Record<MetadataField, string>);
                      draft.reset();
                      setOpen(true);
                    }}><Pencil className="h-4 w-4" /> Edit</Button></div></td>
                  </tr>
                ))
              ) : (
                <tr><td className="px-4 py-8 text-center text-muted-foreground" colSpan={7}>
                  {[companyFilter, stateFilter, physicalStateFilter, regionFilter, typeFilter, list.search.trim()].some(Boolean)
                    ? "No substations match the current filter." : "No substations found."}
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
        <ListPagination {...list} />
      </Card>
      </AdminListLayout>
    </>
  );
}
